import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Scissors, Search } from 'lucide-react';
import {
  ALTERATION_STATUSES,
  isOpenAlteration,
  type AlterationStatus,
} from '@tailoring-crm/shared';
import { AlterationNextStep } from '../components/alterations/alteration-next-step.js';
import { WalkInAlterationDialog } from '../components/alterations/walk-in-alteration-dialog.js';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { Badge, Button, Input, ListSkeleton } from '@tailoring-crm/ui';
import { useAuth } from '../lib/auth/auth-context.js';
import { useAlterations } from '../lib/alterations/use-alterations.js';
import { formatCurrency, formatDate, formatMobile } from '../lib/format.js';

/**
 * The work that came back.
 *
 * A list rather than a board, and deliberately: an alteration has three live states, so columns
 * would be three short piles a shop scrolls past. What it actually needs is "what do I owe, in
 * the order I promised it", which is a list sorted by date with one button on each row.
 *
 * THE ROW'S ONE BUTTON IS THE NEXT STEP. Not a dropdown of every state — there is only ever one
 * forward move, and offering a menu to choose it would be four taps where one will do.
 *
 * A WALK-IN IS BOOKED FROM HERE. A garment the shop stitched is booked back from its order; one
 * it did not has no order to start from, so this screen's header is where the counter starts.
 * A tailor is not offered it, because booking carries a price and the API refuses them.
 */

const OPEN_FILTER = 'open';
type StatusFilter = typeof OPEN_FILTER | AlterationStatus;

const FILTERS: readonly StatusFilter[] = [OPEN_FILTER, ...ALTERATION_STATUSES];

export function AlterationsPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { role } = useAuth();

  const [filter, setFilter] = useState<StatusFilter>(OPEN_FILTER);
  const [query, setQuery] = useState('');
  const [bookingWalkIn, setBookingWalkIn] = useState(false);

  const alterations = useAlterations({
    ...(filter === OPEN_FILTER ? { openOnly: true } : { status: filter }),
    ...(query.trim() ? { q: query.trim() } : {}),
  });

  const rows = alterations.data?.items ?? [];

  return (
    <>
      <PageHeader
        title={t('alterations.title')}
        description={t('alterations.description')}
        action={
          role !== 'tailor' ? (
            <Button onClick={() => setBookingWalkIn(true)}>
              <Plus aria-hidden="true" />
              {t('alterations.walkIn.new')}
            </Button>
          ) : null
        }
      />

      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <Input
            type="search"
            aria-label={t('alterations.searchLabel')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t('alterations.searchPlaceholder')}
            className="pl-9"
          />
        </div>

        {/* Scrolls sideways on a phone rather than wrapping into rows of chips. */}
        <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1">
          {FILTERS.map((one) => (
            <button
              key={one}
              type="button"
              role="tab"
              aria-selected={filter === one}
              onClick={() => setFilter(one)}
              className={
                filter === one
                  ? 'shrink-0 rounded-full border border-transparent bg-primary px-3 py-1.5 text-sm text-primary-foreground'
                  : 'shrink-0 rounded-full border border-border px-3 py-1.5 text-sm hover:bg-accent'
              }
            >
              {one === OPEN_FILTER ? t('alterations.filter.open') : t(`alterations.status.${one}`)}
            </button>
          ))}
        </div>

        {alterations.isLoading ? (
          <ListSkeleton rows={4} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Scissors}
            title={t('alterations.emptyTitle')}
            description={t('alterations.emptyDescription')}
          />
        ) : (
          <ul className="flex flex-col gap-2">
            {rows.map((alteration) => (
              <li
                key={alteration.id}
                className="flex flex-col gap-3 rounded-lg border border-border bg-card p-3 sm:flex-row sm:items-center"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-center gap-2 font-medium">
                    <Link to={`/alterations/${alteration.id}`} className="hover:underline">
                      {alteration.customerName}
                    </Link>
                    <Badge variant={isOpenAlteration(alteration.status) ? 'default' : 'secondary'}>
                      {t(`alterations.status.${alteration.status}`)}
                    </Badge>
                    {/* Zero is the ordinary case, so only a charge is worth showing — and once
                        it is, what is still owed on it, which is what the counter asks. */}
                    {alteration.chargePaise > 0 ? (
                      <Badge variant="outline">
                        {alteration.outstandingPaise > 0
                          ? t('alterations.balanceDue', {
                              amount: formatCurrency(alteration.outstandingPaise),
                            })
                          : formatCurrency(alteration.chargePaise)}
                      </Badge>
                    ) : null}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {alteration.garmentTypeLabel} — {alteration.complaint}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    <Link to={`/alterations/${alteration.id}`} className="hover:underline">
                      {alteration.number}
                    </Link>{' '}
                    ·{' '}
                    {alteration.orderId ? (
                      <Link to={`/orders/${alteration.orderId}`} className="hover:underline">
                        {alteration.orderNumber}
                      </Link>
                    ) : (
                      t('alterations.walkIn.badge')
                    )}{' '}
                    · {formatMobile(alteration.customerPhone)}
                    {alteration.promisedDate
                      ? ` · ${t('alterations.due', { date: formatDate(alteration.promisedDate) })}`
                      : ''}
                  </p>
                </div>

                <AlterationNextStep alteration={alteration} />
              </li>
            ))}
          </ul>
        )}
      </div>

      <WalkInAlterationDialog
        open={bookingWalkIn}
        onOpenChange={setBookingWalkIn}
        onBooked={(booked) => navigate(`/alterations/${booked.id}`)}
      />
    </>
  );
}

export default AlterationsPage;
